import { SYSTEM_SENT_URL } from "../api/mailbox";
import type { UserRole } from "../api/types";

// 哪一個角色看得到哪一個頁面(第十二輪,設計規格 §2.3)。
//
// **這一份是全系統唯一的那一份。** 這個系統有兩個全域導覽面:左邊的側邊欄
// (`Sidebar.tsx`)與 ⌘K 指令面板(`features/command/CommandPalette.tsx`,
// 由 `AppShell` 無條件掛著,每個角色都按得出來)。兩邊各寫一份清單的下場,
// 第十二輪已經發生過一次:側邊欄過濾了、⌘K 沒有,財務按 ⌘K 照樣點得進品項庫,
// 進去是一頁載不出來的品項庫(後端回 403)。
//
// 所以角色寫在**路徑**上,兩個導覽面都問同一支 `canSeeNav()`。加一頁就是加一列,
// 只有一個地方要改;第 15 輪長出發票、雜支與報表(老闆與財務看得到、同仁看不到)
// 時,也只有這裡要動。

/**
 * 全員看得到的頁面。報價單對財務是唯讀的,但**看得到** ——
 * 一個看不到報價金額的財務,連發票對不對得上都查不了(設計規格 §2.1);
 * 請假則是因為「但他也是員工 所以他也要可以申請請假」(業主原話,§2.4)。
 */
export const EVERYONE: readonly UserRole[] = ["OWNER", "STAFF", "FINANCE"];

/**
 * 業務功能。品項庫、客戶、範本與設定對財務沒有任何意義,
 * 「新增報價單」更是一個寫入動作(後端 `require_business`,財務送出就是 403)。
 *
 * **整個頁面與這個角色無關,就不該出現在導覽上**(設計規格 §2.3)——
 * 擺一排點進去說「你沒有權限」的項目,只會讓人一直來問為什麼別人進得去。
 * 這與封存區那顆「刪除」對員工是 disabled 不矛盾:那是**同一個頁面裡的一個
 * 動作**做不得,要畫出來並說明原因(不然使用者會找不到它在哪);
 * 這裡整頁都不屬於這個角色,分界就在「整頁」還是「一個動作」。
 */
export const BUSINESS: readonly UserRole[] = ["OWNER", "STAFF"];

/**
 * 財務的櫃檯。發票、雜支與報表是財務的本業,**同仁看不到** ——
 * 應收帳款與營收不是業務該在側邊欄上看到的東西。
 *
 * 老闆在裡面,因為**老闆是所有權限的聯集**(設計規格 §2.1):
 * 凡是財務做得到的老闆都做得到,不必另外開一個財務帳號給自己。
 *
 * 後端對應的守門是 `require_finance`(老闆或財務),不是放寬後的
 * `require_owner` —— 兩支並存,語意各自清楚。
 */
export const FINANCE_DESK: readonly UserRole[] = ["OWNER", "FINANCE"];

/** 每一個導覽目的地看得到它的角色。側邊欄與 ⌘K 共用這一份。 */
export const NAV_ROLES: Readonly<Record<string, readonly UserRole[]>> = {
  // 首頁與信箱是全員的(業主 2026-09-17)——
  // 首頁是每個人登入後的落點,信箱是公司共用的收件匣。
  "/home": EVERYONE,
  "/mailbox": EVERYONE,
  // 回收桶是**全員**的(第 24 輪):每個人都要救得回自己刪掉的東西,
  // 而他看得到的本來就只有自己丟的那幾筆(後端 `app/routers/trash.py`)。
  // 出勤紀錄是**全員**的:每個人看自己的(後端擋著,帶別人的 id 回 403)。
  "/attendance": EVERYONE,
  "/trash": EVERYONE,
  "/quotes": EVERYONE,
  // 「新增報價單」不是一個頁面而是一個入口,但它通往的是一張只有業務動得了的
  // 空白單 —— 畫給財務只會讓他填完整張才收到 403。
  "/quotes/new": BUSINESS,
  "/templates": BUSINESS,
  "/catalog": BUSINESS,
  "/companies": BUSINESS,
  // 製單(業主 2026-10-08):**全員**的 —— 規格寫「全部同仁都能開、都看得到」,財務也開工單。
  "/workorders": EVERYONE,
  // 「系統寄出」是信箱裡的一個資料夾(寄送是業務動作,財務那邊永遠是空的),只有指令面板會用到這個鍵
  [SYSTEM_SENT_URL]: BUSINESS,
  // 會議紀錄是**全員**的(業主原話:「任何同仁都可以」)——
  // 財務也看得到,而且會議紀錄裡沒有他碰不得的東西。
  "/meetings": EVERYONE,
  // 公佈欄是**全員**的(業主 2026-09-25:「布告欄內容每個人都可以新增/移除」)。
  "/bulletin": EVERYONE,
  "/invoices": FINANCE_DESK,
  "/expenses": FINANCE_DESK,
  // 對帳的兩張總表 —— 申報數字,只有老闆與財務。
  "/reconcile": FINANCE_DESK,
  "/reports": FINANCE_DESK,
  "/leave": EVERYONE,
  // 設定是**全員**的(2026-09-25):裡面有每個人自己的「個人資料」。
  // 公司資料對老闆以外唯讀、使用者分頁只有老闆看得到 —— 那是頁面裡的事。
  "/settings": EVERYONE,
  // 更新紀錄與留言板是**全員**的(業主 2026-09-28:「這個功能所有人開放」)。
  "/updates": EVERYONE,
};

/**
 * 這個角色看不看得到這條導覽。
 *
 * 兩種「不知道」一律放行,理由不同但結論相同:
 *
 * - **還不知道是誰**(`null` / `undefined`):真實動線裡兩個導覽面都在
 *   `RequireAuth` 之後才掛載(`App.tsx`),`user` 是 null 只會發生在單獨 render
 *   外框的測試裡 —— 那時把導覽整個清空,等於逼每一個外框測試都先假造登入狀態。
 * - **表上沒有這條路徑**:導覽只負責「不把沒意義的頁面擺在人家眼前」,
 *   真正擋權限的是後端每一支端點。表上漏了一列不該讓畫面整個空掉。
 */
export function canSeeNav(path: string, role: UserRole | null | undefined): boolean {
  if (role === null || role === undefined) return true;
  const roles = NAV_ROLES[path];
  return roles === undefined ? true : roles.includes(role);
}
